import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  openEditorModal,
  RpEditorConfig,
  RpEditorResult,
  RpEditorTheme,
  ModalOptions,
  LanguageCode,
} from '@rageshpikalmunde/rp-image-editor';

/**
 * Configuration presets for common editor use-cases.
 */
export interface ImageEditorPreset {
  config: Partial<RpEditorConfig>;
}

/**
 * Result wrapper returned by the service.
 * Includes the original file reference for traceability.
 */
export interface EditedImageResult {
  /** The RpEditorResult returned by the plugin (null when cancelled). */
  editorResult: RpEditorResult | null;
  /** Convenience File built from the editor output, preserving the original file name. */
  file: File | null;
  /** Whether the user cancelled the editor. */
  cancelled: boolean;
}

/**
 * Centralized Angular service that wraps `@rageshpikalmunde/rp-image-editor`.
 *
 * Benefits:
 *  - Single place to define / change default config, theme, and presets.
 *  - Correctly calls `openEditorModal(options: ModalOptions)` with a single arg.
 *  - Easy to inject & mock in tests.
 *  - Re-usable across any component that needs image editing.
 */
@Injectable({
  providedIn: 'root',
})
export class ImageEditorService {
  constructor(private translate: TranslateService) {}

  // ────────────────────────────────────────────
  //  Default configuration  (change here once → applies everywhere)
  // ────────────────────────────────────────────

  private readonly defaultConfig: Partial<RpEditorConfig> = {
    exportFormat: 'jpeg',
    exportQuality: 0.92,
    maxUndoSteps: 20,
    defaultBrushWidth: 10,
    defaultShapeStrokeWidth: 10,
    defaultTextFontSize: 24,
    disabledFeatures: ['move', 'filters', 'adjust'], // e.g. ['rotateLeft', 'rotateRight', 'reset'] to hide transform tools
    cropAspectRatios: [{ label: '4:3', value: 4 / 3 }, { label: '3:4', value: 3 / 4 }],
    exportAtNativeResolution: true,
    labels: {
      tool: {
        pen: this.translate.instant('draw'),
        callout: this.translate.instant('label'),
        move: this.translate.instant('select')
      },
    },
  };

  private readonly defaultTheme: RpEditorTheme = {
    headerTitle: this.translate.instant('image_editor'),
    headerLogo: '../../../assets/images/schmersal.png', // Optional logo URL
    headerBadgeSize: 40,   // was 40 — makes the tile bigger
    headerLogoSize:  32,
    // Matches demo styling; `50px` turned small square icon buttons into pills/circles.
    buttonBorderRadius: '8px',
    // Light palette — required so slider tracks, borders, and hover states
    // use light-scheme tokens (default `dark` renders tracks in a near-black
    // that disappears against the customized light backgrounds below).
    variant: 'light',
    headerBackground: '#ffffff',
    headerTextColor: '#333333',
    editorBackground: '#f5f5f5',
    toolbarBackground: '#e0e0e0',
    toolbarIconColor: '#333333',
    toolbarActiveBackground: '#083a81',
    toolbarActiveTextColor: '#ffffff',
    footerBackground: '#ffffff',
    cancelButtonTextColor: '#333333',
    cancelButtonBorderColor: '#999999',
    applyButtonBackground: '#083a81',
    applyButtonBorderColor: '#083a81',
    // Modal frame size overrides (library defaults: 1200px × min(92vh, 820px)).
    // Reduce the editor panel footprint so it feels less overwhelming on
    // typical laptop screens while still leaving room for the canvas.
    modalMaxWidth: 'min(90vw, 1000px)',
    modalHeight: 'min(85vh, 680px)',
    
  };

  // ────────────────────────────────────────────
  //  Named presets
  // ────────────────────────────────────────────

  private presets: Record<string, ImageEditorPreset> = {
    hazardImage: {
      config: {
        exportFormat: 'jpeg',
        exportQuality: 0.92,
      },
    },
    highQuality: {
      config: {
        exportFormat: 'png',
        exportQuality: 1,
      },
    },
  };

  // ────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────

  /**
   * Open the image editor modal for a single file.
   *
   * @param image     - The File/Blob/dataURL to edit.
   * @param overrides - Optional per-call config overrides.
   * @returns An `EditedImageResult` (never throws; `cancelled = true` on cancel).
   */
  async open(
    image: File | Blob | string,
    overrides?: Partial<RpEditorConfig>,
  ): Promise<EditedImageResult> {
    const defaultLabels = this.defaultConfig.labels || {};
    const overrideLabels = overrides?.labels || {};

    const mergedConfig: Partial<RpEditorConfig> = {
      ...this.defaultConfig,
      ...overrides,
      language: overrides?.language || this.resolveEditorLanguage(),
      theme: {
        ...this.defaultTheme,
        ...(overrides?.theme || {}),
      },
      labels: {
        ...defaultLabels,
        ...overrideLabels,
        tool: {
          ...(defaultLabels.tool || {}),
          ...(overrideLabels.tool || {}),
        },
        props: {
          ...(defaultLabels.props || {}),
          ...(overrideLabels.props || {}),
          title: {
            ...(defaultLabels.props?.title || {}),
            ...(overrideLabels.props?.title || {}),
          },
        },
      },
    };

    const options: ModalOptions = {
      image,
      config: mergedConfig,
    };

    try {
      const result = await openEditorModal(options);

      if (result && result.file) {
        // Preserve the original name when the source is a File
        const originalName =
          image instanceof File ? image.name : 'edited-image';
        const editedFile = new File(
          [result.file],
          originalName,
          { type: result.file.type },
        );

        return { editorResult: result, file: editedFile, cancelled: false };
      }

      // User cancelled
      return { editorResult: null, file: null, cancelled: true };
    } catch (error) {
      console.error('[ImageEditorService] Editor error:', error);
      // Return the original as fallback when possible
      if (image instanceof File) {
        return { editorResult: null, file: image, cancelled: false };
      }
      return { editorResult: null, file: null, cancelled: false };
    }
  }

  /**
   * Open the editor using a named preset.
   *
   * @param image      - The image to edit.
   * @param presetName - Key of a registered preset (e.g. `'hazardImage'`).
   * @param overrides  - Extra per-call overrides layered on top of the preset.
   */
  async openWithPreset(
    image: File | Blob | string,
    presetName: string,
    overrides?: Partial<RpEditorConfig>,
  ): Promise<EditedImageResult> {
    const preset = this.presets[presetName];
    const presetConfig = preset ? preset.config : {};
    return this.open(image, { ...presetConfig, ...overrides });
  }

  /**
   * Process multiple files through the editor sequentially.
   * Returns only non-cancelled, successfully edited files.
   *
   * @param files     - Array of files to edit one-by-one.
   * @param overrides - Optional per-call config overrides.
   */
  async openBatch(
    files: File[],
    overrides?: Partial<RpEditorConfig>,
  ): Promise<File[]> {
    const editedFiles: File[] = [];
    const totalImages = files.length;
    const hasMultipleImages = totalImages > 1;

    for (const [index, file] of files.entries()) {
      const batchOverrides: Partial<RpEditorConfig> = hasMultipleImages
        ? {
            ...overrides,
            currentImageIndex: index + 1,
            totalImages,
          }
        : overrides;

      const result = await this.open(file, batchOverrides);
      if (!result.cancelled && result.file) {
        editedFiles.push(result.file);
      }
    }

    return editedFiles;
  }

  /**
   * Register or update a named configuration preset at runtime.
   */
  registerPreset(name: string, preset: ImageEditorPreset): void {
    this.presets[name] = preset;
  }

  /**
   * Update the default theme applied to every editor instance.
   */
  updateDefaultTheme(theme: Partial<RpEditorTheme>): void {
    Object.assign(this.defaultTheme, theme);
  }

  /**
   * Update the default config applied to every editor instance.
   */
  updateDefaultConfig(config: Partial<RpEditorConfig>): void {
    Object.assign(this.defaultConfig, config);
  }

  /**
   * Resolves the active app language into a `LanguageCode` accepted by the
   * image editor. The editor treats `sp` as an alias for `es` and folds
   * regional variants to their primary tag, so passing the raw ngx-translate
   * code is safe.
   */
  private resolveEditorLanguage(): LanguageCode {
    const active =
      this.translate?.currentLang ||
      this.translate?.defaultLang ||
      'en';
    return active as LanguageCode;
  }
}
